/**
 * A shape, as the board stores it and as the other tools see it.
 *
 * Stored as a board object (docs/05-DATA-MODEL.md §3.2 and §3.3) because that is what
 * a shape is: a placed thing with a type, a size and a position, not a line of ink.
 *
 * Read back, though, it is offered to the eraser and the select tool as if it were a
 * stroke — same id, same outline, same colour, same canvas-space width. Those tools
 * already know how to rub out, move, resize, turn and recolour a list of points, and
 * a shape is a list of points. Nothing about them had to change to work on shapes.
 */

import {
  newObjectId,
  type BoardObject,
  type Layer,
  type Point,
  type Rect,
  type ShapeData,
  type ShapeKind,
  type ShapeType,
  type Stroke,
} from '@opal/shared';

import { readNumber, readString } from './object-data.js';

/** Which module owns shape objects — docs/04-MODULE-CONTRACTS.md §3.1.1. */
export const SHAPES_MODULE = 'shapes';

/** Ids from `newObjectId`, so a shape can be told from a stroke by its id alone. */
const OBJECT_PREFIX = 'obj_';

export function isObjectId(id: string): boolean {
  return id.startsWith(OBJECT_PREFIX);
}

const strokeViews = new WeakMap<BoardObject, Stroke>();

function isPoint(value: unknown): value is Point {
  if (typeof value !== 'object' || value === null) return false;
  const candidate: Record<string, unknown> = { ...value };
  return typeof candidate.x === 'number' && typeof candidate.y === 'number';
}

/** The outline the object carries, or an empty list if it has none. */
export function outlinePointsOf(object: BoardObject): Point[] {
  const value = object.data.points;
  if (!Array.isArray(value)) return [];
  return value.filter(isPoint).map((point) => ({ x: point.x, y: point.y }));
}

/** True when this object is a shape this module drew. */
export function isShape(object: BoardObject): boolean {
  return object.kind === 'shape';
}

/** The box an outline covers, before any allowance for the width of the line. */
export function outlineBounds(points: readonly Point[]): Rect {
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * The stored type for a shape the teacher chose.
 *
 * A circle dragged into an oval is stored as an ellipse, and a rectangle dragged
 * square as a square, because the data model has words for both and a lecture that
 * says what it actually contains is worth more later than one that does not.
 */
export function shapeTypeOf(kind: ShapeKind, bounds: Rect): ShapeType {
  const square = Math.abs(bounds.width - bounds.height) < 0.5;
  if (kind === 'circle') return square ? 'circle' : 'ellipse';
  if (kind === 'rectangle') return square ? 'square' : 'rectangle';
  return kind;
}

export interface NewShape {
  readonly kind: ShapeKind;
  readonly points: readonly Point[];
  readonly userId: string;
  readonly color: string;
  /** Canvas-space, exactly like `Stroke.canvasWidth` (SRS C3). */
  readonly canvasWidth: number;
  readonly layer: Layer;
  /** Where it sits in the stack. Defaults to the moment it was drawn: newest on top. */
  readonly zIndex?: number;
  /** Only meaningful for a polygon. */
  readonly sides?: number;
  /**
   * What the shape is, when the caller already knows better than the box does.
   *
   * The shapes tool does not: it drags a box, so a square is a rectangle whose sides
   * came out equal. The Smart Pen does — it has just recognised a hexagon, and a
   * hexagon tilted on the board has a box that says nothing about it.
   */
  readonly shapeType?: ShapeType;
}

/** A finished shape, ready to go on the board and to be shared. */
export function buildShape(input: NewShape): BoardObject {
  const bounds = outlineBounds(input.points);
  const now = Date.now();
  const data: ShapeData = {
    shapeType: input.shapeType ?? shapeTypeOf(input.kind, bounds),
    stroke: input.color,
    // SRS C4 gives the shapes tool no fill control, so a shape is an outline in V1.
    fill: null,
    strokeCanvasWidth: input.canvasWidth,
    points: input.points.map((point) => ({ x: point.x, y: point.y })),
    ...(input.kind === 'polygon' && input.sides ? { sides: input.sides } : {}),
  };

  return {
    id: newObjectId(),
    kind: 'shape',
    createdByModule: SHAPES_MODULE,
    userId: input.userId,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    // The outline itself carries any turning, so the record never has to.
    rotation: 0,
    zIndex: input.zIndex ?? now,
    layer: input.layer,
    locked: false,
    data: { ...data },
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * The shape as a stroke: the same outline, at full pressure throughout.
 *
 * Kept for as long as the object lives, because every tool that touches a shape asks
 * for this and building a new record each time would throw away the measurements the
 * board has already made of it.
 */
export function shapeStroke(object: BoardObject): Stroke {
  const cached = strokeViews.get(object);
  if (cached) return cached;

  const points = outlinePointsOf(object);
  const flat = new Float32Array(points.length * 3);
  points.forEach((point, index) => {
    flat[index * 3] = point.x;
    flat[index * 3 + 1] = point.y;
    // A shape has an even line: no pressure, no thick and thin.
    flat[index * 3 + 2] = 1;
  });

  const view: Stroke = {
    id: object.id,
    userId: object.userId,
    tool: 'pen',
    points: flat,
    canvasWidth: readNumber(object.data, 'strokeCanvasWidth', 1),
    color: readString(object.data, 'stroke', '#000000'),
    opacity: 1,
    layer: object.layer,
    createdAt: object.createdAt,
  };
  strokeViews.set(object, view);
  return view;
}

/** The points of a stroke, back as plain canvas points. */
function pointsOfStroke(stroke: Stroke): Point[] {
  const points: Point[] = [];
  for (let index = 0; index + 2 < stroke.points.length; index += 3) {
    points.push({ x: stroke.points[index] ?? 0, y: stroke.points[index + 1] ?? 0 });
  }
  return points;
}

/**
 * The object a changed stroke view describes: moved, resized, turned or recoloured.
 *
 * Everything the shape is beyond its outline — what type it is, how many sides it
 * has — is kept exactly as it was. A moved pentagon is still a pentagon.
 */
export function shapeFromStroke(object: BoardObject, stroke: Stroke): BoardObject {
  const points = pointsOfStroke(stroke);
  const bounds = outlineBounds(points);
  return {
    ...object,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    data: {
      ...object.data,
      stroke: stroke.color,
      strokeCanvasWidth: stroke.canvasWidth,
      points,
    },
    updatedAt: Date.now(),
  };
}
